import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Stethoscope, ShoppingBag, Heart, Users, Building2, Sparkles, PawPrint, Calendar, ArrowRight, Gift, MessageSquare, Bell, Syringe, FileText, Truck, Scissors, MapPin, ShieldCheck, Activity, Wallet, ClipboardList, Crown, Search } from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [appts, setAppts] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    // Redirect vendors to their dashboard
    if (user?.role === "vet") return navigate("/app/vet-dashboard", { replace: true });
    if (user?.role === "store_owner") return navigate("/app/store-dashboard", { replace: true });
    if (user?.role === "hostel_owner") return navigate("/app/hostel-dashboard", { replace: true });
    if (user?.role === "ngo") return navigate("/app/ngo/impact", { replace: true });
    if (user?.role === "groomer" || user?.role === "walker") return navigate("/app/services-dashboard", { replace: true });
    // super_admin stays on home unless they explicitly visit /app/admin
  }, [user, navigate]);

  useEffect(() => {
    axios.get(`${API}/pets`).then(r => setPets(r.data)).catch(()=>{});
    axios.get(`${API}/appointments`).then(r => setAppts(r.data)).catch(()=>{});
    axios.get(`${API}/posts`).then(r => setPosts(r.data.slice(0,2))).catch(()=>{});
  }, []);

  const upcoming = appts.filter(a => a.status === "upcoming").slice(0, 2);

  // Match deck slide 4: 4 BIG primary tiles → Pets (Meetups), Doctors (Vets), Store, Adopt
  const MAIN = [
    { to: "/app/meetups", title: "Pets", subtitle: "Find Meetups", icon: PawPrint, color: "bg-brand", img: "https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?w=600&q=80", testid: "main-tile-pets" },
    { to: "/app/vets", title: "Doctors", subtitle: "Vet Consults", icon: Stethoscope, color: "bg-sky", img: "https://images.pexels.com/photos/7468981/pexels-photo-7468981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=720", testid: "main-tile-doctors" },
    { to: "/app/store", title: "Store", subtitle: "Pet Supplies", icon: ShoppingBag, color: "bg-sunshine", img: "https://images.unsplash.com/photo-1549297161-14f79605a74c?w=600&q=80", testid: "main-tile-store" },
    { to: "/app/adoption", title: "Adopt", subtitle: "Buy / Sell Pets", icon: Heart, color: "bg-leaf", img: "https://images.unsplash.com/photo-1517849845537-4d257902454a?w=600&q=80", testid: "main-tile-adopt" },
  ];

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Welcome banner — matches deck "Welcome, Ravi!" */}
      <section className="rounded-3xl p-6 md:p-10 bg-gradient-to-br from-brand to-brand-hover text-white relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <div className="text-xs font-bold tracking-[0.18em] uppercase opacity-80">Welcome back 👋</div>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold leading-tight mt-2">
            Hi {user?.name?.split(" ")[0]}!<br/>
            <span className="opacity-80 text-2xl md:text-3xl font-bold">Your all-in-one pet app</span>
          </h1>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/app/symptom-checker" data-testid="home-ai-cta" className="bg-white text-ink rounded-full px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
              Ask AI Vet Buddy <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/app/vets" data-testid="home-book-vet-cta" className="bg-ink text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-black/80 transition-colors">
              Book a Vet
            </Link>
          </div>
        </div>
        <PawPrint className="absolute right-8 top-8 w-20 h-20 opacity-15" />
        <div className="absolute -right-10 -bottom-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
      </section>

      {/* FEATURED — Pet Registration concierge (NEW) */}
      <Link
        to="/app/pet-registration"
        data-testid="home-featured-petreg"
        className="block rounded-3xl bg-gradient-to-r from-leaf via-sun/40 to-brand/15 border border-brand/30 p-5 md:p-6 hover:-translate-y-0.5 hover:shadow-soft transition-all"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-brand text-white grid place-items-center shadow-soft shrink-0">
            <FileText className="w-7 h-7"/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-widest uppercase">
              <Sparkles className="w-3 h-3"/> New · Concierge
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-extrabold mt-2 leading-tight">Pet licence registration — done for you</h3>
            <p className="text-sm text-ink-soft mt-1">Noida · Delhi NCR · Mumbai · Bengaluru · Pune · Chennai · Hyderabad + pan-India · Flat ₹999 service fee · Certificate to your inbox</p>
          </div>
          <div className="bg-ink text-white rounded-full px-4 py-2 font-bold text-sm inline-flex items-center gap-2 self-center">
            Start <ArrowRight className="w-4 h-4"/>
          </div>
        </div>
      </Link>

      {/* 4 main tiles — matches deck slide 4 layout */}
      <section data-testid="main-tiles">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {MAIN.map(t => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="group relative rounded-3xl overflow-hidden bg-surface border border-line shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all h-44 md:h-56"
            >
              <img src={t.img} alt={t.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"/>
              <div className="absolute top-4 left-4">
                <div className={`w-10 h-10 rounded-2xl grid place-items-center shadow-soft ${t.color} ${t.color === "bg-sunshine" || t.color === "bg-leaf" ? "text-ink" : "text-white"}`}>
                  <t.icon className="w-5 h-5"/>
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="font-heading text-2xl font-extrabold leading-none">{t.title}</div>
                <div className="text-xs uppercase tracking-wider font-bold opacity-90 mt-1">{t.subtitle}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* My Pets row */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold">My Pets</h2>
          <Link to="/app/pets" className="text-sm font-bold text-brand">Manage →</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {pets.map(p => (
            <div key={p.pet_id} data-testid={`pet-chip-${p.pet_id}`} className="shrink-0 bg-surface border border-line rounded-3xl p-2 pr-4 flex items-center gap-3 shadow-soft">
              <img src={p.photo} className="w-12 h-12 rounded-2xl object-cover" alt={p.name} />
              <div>
                <div className="font-heading font-bold">{p.name}</div>
                <div className="text-xs text-ink-soft">{p.breed} · {p.age_years}y</div>
              </div>
            </div>
          ))}
          <Link to="/app/pets" className="shrink-0 rounded-3xl border-2 border-dashed border-line px-5 grid place-items-center text-ink-soft text-sm font-bold hover:border-brand hover:text-brand transition-colors">
            + Add pet
          </Link>
        </div>
      </section>

      {/* Appointments + Activity Feed split (deck slide 5) */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Calendar className="w-4 h-4 text-sky"/> Appointments</h3>
            <Link to="/app/appointments" className="text-xs font-bold text-brand">All →</Link>
          </div>
          {upcoming.length === 0 && <div className="text-ink-soft text-sm">No upcoming visits.</div>}
          <div className="space-y-2">
            {upcoming.map(a => (
              <div key={a.appointment_id} className="bg-surface-muted rounded-2xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky/15 text-sky grid place-items-center"><Syringe className="w-4 h-4"/></div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{a.service} · {a.pet_name}</div>
                  <div className="text-xs text-ink-soft">{a.vet_name} · {new Date(a.date_time).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-brand"/> Activity Feed</h3>
            <Link to="/app/feed" className="text-xs font-bold text-brand">All →</Link>
          </div>
          <div className="space-y-2">
            {posts.map(p => (
              <div key={p.post_id} className="bg-surface-muted rounded-2xl p-3 flex items-start gap-3">
                {p.author_picture ? <img src={p.author_picture} className="w-9 h-9 rounded-full object-cover" alt=""/> : <div className="w-9 h-9 rounded-full bg-sky grid place-items-center text-white text-sm font-bold">{p.author?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{p.author}</div>
                  <div className="text-xs text-ink-soft line-clamp-2">{p.content}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All features — categorised so users discover every function */}
      <TileGroup title="Services nearby" sub="Same-day grooming, walks & boarding" tiles={[
        { to: "/app/services", title: "Grooming & Walks", icon: Scissors, color: "bg-brand text-white", text: "Groomers · walkers · pickup" },
        { to: "/app/hostels", title: "Pet Hostels", icon: Building2, color: "bg-ink text-white", text: "Verified boarding stays" },
        { to: "/app/van-schedule", title: "Mobile Vet Van", icon: Truck, color: "bg-sky text-white", text: "Vaccination drives near you" },
        { to: "/app/foster", title: "Foster", icon: Heart, color: "bg-leaf text-ink", text: "Short-term shelter help" },
      ]}/>

      <TileGroup title="Health & identity" sub="Round-the-clock care your pet deserves" tiles={[
        { to: "/app/symptom-checker", title: "AI Vet Buddy", icon: Sparkles, color: "bg-brand text-white", text: "24/7 symptom check" },
        { to: "/app/pet-id", title: "Digital Pet ID", icon: ShieldCheck, color: "bg-ink text-white", text: "Microchip · QR · cert" },
        { to: "/app/tracking", title: "Live Tracking", icon: MapPin, color: "bg-sky text-white", text: "Walks & GPS history" },
        { to: "/app/lost-pets", title: "Lost & Found", icon: Search, color: "bg-sunshine text-ink", text: "Report & spot lost pets" },
      ]}/>

      <TileGroup title="Community & rescue" sub="Be part of India's largest pet family" tiles={[
        { to: "/app/meetups", title: "Pet Meetups", icon: PawPrint, color: "bg-brand text-white", text: "Walks & playdates" },
        { to: "/app/feed", title: "Community Feed", icon: MessageSquare, color: "bg-ink text-white", text: "Stories from the pack" },
        { to: "/app/ngos", title: "NGOs & Shelters", icon: Heart, color: "bg-sky text-white", text: "Donate · 80G receipts" },
        { to: "/app/adoption", title: "Adopt or Rehome", icon: Heart, color: "bg-leaf text-ink", text: "Find a forever home" },
      ]}/>

      <TileGroup title="My account" sub="Orders, perks & shortcuts" tiles={[
        { to: "/app/my-orders", title: "My Orders", icon: ClipboardList, color: "bg-brand text-white", text: "Store deliveries · status" },
        { to: "/app/billing", title: "Premium & Billing", icon: Crown, color: "bg-sunshine text-ink", text: "Plan · invoices · payments" },
        { to: "/app/refer", title: "Refer & Earn", icon: Gift, color: "bg-leaf text-ink", text: "1 free month each" },
        { to: "/app/appointments", title: "Appointments", icon: Calendar, color: "bg-ink text-white", text: "Past & upcoming visits" },
      ]}/>

      {/* Partner CTA */}
      <section>
        <Link to="/app/partner" data-testid="home-partner-cta" className="block rounded-3xl bg-ink text-white p-6 md:p-8 hover:bg-black transition-colors">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-70">For businesses</div>
              <div className="font-heading text-2xl font-extrabold mt-1">Are you a vet, store, or hostel owner?</div>
              <div className="text-sm opacity-80 mt-1">Open your partner dashboard and start receiving pet parents on PetBond.</div>
            </div>
            <div className="bg-brand rounded-full px-5 py-2.5 font-bold text-sm inline-flex items-center gap-2">
              Become a partner <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </Link>
      </section>
    </div>
  );
}

function TileGroup({ title, sub, tiles }) {
  return (
    <section data-testid={`tile-group-${title.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-heading text-xl font-bold">{title}</h2>
        {sub && <span className="text-xs text-ink-soft font-bold">{sub}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map(t => (
          <Link key={t.to} to={t.to} data-testid={`tile-${t.title.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`} className="bg-surface border border-line rounded-3xl p-5 shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
            <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-3 ${t.color}`}><t.icon className="w-5 h-5"/></div>
            <div className="font-heading font-bold">{t.title}</div>
            <div className="text-xs text-ink-soft mt-0.5">{t.text}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
